import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { formatPostedDateMontreal, getJobBySlug } from './jobsData';

export default function JobDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const job = getJobBySlug(slug);

  if (!job) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <main className="pt-28 pb-24 px-6 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">{t('careers.jobNotFound')}</h1>
          <Link to="/careers" className="text-[#9F7B5B] font-medium underline underline-offset-2">
            {t('careers.backToCareers')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const postedDateFormatted = formatPostedDateMontreal(
    i18n.language,
    job.posted.year,
    job.posted.month,
    job.posted.day
  );

  const applyMailto =
    'mailto:careers@flexygroup.ca?subject=' + encodeURIComponent(job.applySubject);

  const details = [
    { icon: 'ri-map-pin-line', label: t('careers.detail.location'), value: job.location },
    { icon: 'ri-briefcase-line', label: t('careers.detail.employment'), value: job.employmentType },
    { icon: 'ri-building-line', label: t('careers.detail.workArrangement'), value: job.workArrangement },
    { icon: 'ri-time-line', label: t('careers.detail.hours'), value: job.hours },
    { icon: 'ri-money-dollar-circle-line', label: t('careers.detail.salary'), value: job.salary },
    { icon: 'ri-user-add-line', label: t('careers.detail.vacancies'), value: job.vacancies },
    { icon: 'ri-calendar-line', label: t('careers.detail.startDate'), value: job.startDate },
    { icon: 'ri-translate-2', label: t('careers.detail.language'), value: job.languageRequirement },
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <main className="pt-28 pb-16">
        <div className="container mx-auto px-6 max-w-3xl">
          <Link
            to="/careers"
            className="inline-flex items-center text-sm font-medium text-[#9F7B5B] hover:text-[#8a6a4f] mb-8"
          >
            <i className="ri-arrow-left-line mr-1 text-lg" aria-hidden />
            {t('careers.backToCareers')}
          </Link>

          <p className="text-sm font-semibold uppercase tracking-wider text-[#9F7B5B] mb-2">
            {t('careers.badge')}
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">{job.title}</h1>

          <div className="flex flex-wrap items-center gap-3 mb-6 text-sm">
            <span
              className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 font-medium text-emerald-800 ring-1 ring-emerald-200/80"
              role="status"
            >
              <span className="mr-1.5 h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
              {t('careers.statusActive')}
            </span>
            <span className="text-gray-600">{t('careers.postedOn', { date: postedDateFormatted })}</span>
          </div>

          <dl className="grid gap-3 sm:grid-cols-2 mb-10 text-sm md:text-base">
            {details.map((item) => (
              <div
                key={item.label}
                className="flex flex-col rounded-xl bg-white px-4 py-3 shadow-sm border border-stone-200"
              >
                <dt className="inline-flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  <i className={`${item.icon} mr-1.5 text-[#9F7B5B]`} aria-hidden />
                  {item.label}
                </dt>
                <dd className="text-gray-800 font-medium leading-snug">{item.value}</dd>
              </div>
            ))}
          </dl>

          <article className="prose prose-stone max-w-none prose-headings:text-gray-900 prose-a:text-[#9F7B5B] prose-strong:text-gray-900">
            <h2 className="text-xl font-semibold mt-10 mb-3">About Flexy Group</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{job.aboutLead}</p>

            <h2 className="text-xl font-semibold mt-10 mb-3">The Opportunity</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{job.opportunity}</p>

            <h2 className="text-xl font-semibold mt-10 mb-3">Key Responsibilities</h2>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-6">
              {job.responsibilities.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            <h2 className="text-xl font-semibold mt-10 mb-3">Required Qualifications</h2>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-6">
              {job.required.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            <h2 className="text-xl font-semibold mt-10 mb-3">Nice to Have</h2>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-6">
              {job.niceToHave.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            <h2 className="text-xl font-semibold mt-10 mb-3">What We Offer</h2>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-6">
              {job.whatWeOffer.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            <h2 className="text-xl font-semibold mt-10 mb-3">How to Apply</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {job.howToApplyLead}{' '}
              <a href={applyMailto} className="font-medium underline underline-offset-2">
                careers@flexygroup.ca
              </a>{' '}
              with the subject line: <strong>{job.applySubject}</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">{job.howToApplyResumeNote}</p>
            <p className="text-gray-700 leading-relaxed mb-4">{job.howToApplyScreening}</p>
            <p className="text-gray-600 text-sm leading-relaxed">{job.equalOpportunity}</p>
          </article>

          <div className="mt-12">
            <a
              href={applyMailto}
              className="inline-flex items-center justify-center rounded-lg bg-[#9F7B5B] px-6 py-3 text-white font-medium shadow-md transition hover:bg-[#8a6a4f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9F7B5B] focus-visible:ring-offset-2"
            >
              {t('careers.applyCta')}
              <i className="ri-mail-send-line ml-2 text-lg" aria-hidden />
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
